import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Sparkles, X } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Site-wide promo banner. Reads /api/promotions/active. Users can dismiss
 * for the current session (sessionStorage). Shows a live countdown if the
 * promotion opts in.
 */
export default function PromoBanner() {
  const [promo, setPromo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    try { setDismissed(sessionStorage.getItem("ff:promo:dismissed") === "1"); } catch {}
    api.get("/api/promotions/active").then(({ data }) => {
      if (data?.active) setPromo(data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!promo?.show_countdown) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [promo?.show_countdown]);

  if (!promo || dismissed) return null;

  const endsAt = promo.ends_at ? new Date(promo.ends_at).getTime() : null;
  const remaining = endsAt ? Math.max(0, endsAt - now) : 0;
  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  const showCountdown = !!promo.show_countdown && endsAt && remaining > 0
                        && days < 365; // don't show countdown for our "no-end" launch promo

  const theme = promo.theme_color || "#00ff9c";

  return (
    <div
      className="w-full relative overflow-hidden border-b border-white/10"
      style={{ background: `linear-gradient(90deg, ${theme}18, transparent 40%, ${theme}18)` }}
      data-testid="promo-banner"
    >
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <Sparkles className="w-4 h-4" style={{ color: theme }} />
        <div className="flex-1 flex items-center gap-3 flex-wrap">
          <span className="text-white text-[13px] font-medium tracking-tight" data-testid="promo-banner-text">
            {promo.banner_text || "Limited-time seasonal sale"}
          </span>
          {showCountdown && (
            <span className="inline-flex items-center gap-1.5 font-mono text-[11px] tabular-nums text-white/85 label-mono" data-testid="promo-countdown">
              Ends in
              <span className="text-white">{days}d</span>
              <span className="text-white">{String(hours).padStart(2,"0")}h</span>
              <span className="text-white">{String(minutes).padStart(2,"0")}m</span>
              <span className="text-white">{String(seconds).padStart(2,"0")}s</span>
            </span>
          )}
          <Link
            to="/pricing"
            className="text-[12px] font-semibold tracking-tight px-2.5 py-1 rounded-full border transition-colors"
            style={{ color: theme, borderColor: `${theme}55`, background: `${theme}18` }}
            data-testid="promo-banner-cta"
          >
            View plans →
          </Link>
        </div>
        <button
          onClick={() => { try { sessionStorage.setItem("ff:promo:dismissed", "1"); } catch {} ; setDismissed(true); }}
          className="text-white/40 hover:text-white p-1"
          aria-label="Dismiss promotion banner"
          data-testid="promo-banner-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
